import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

interface Recommendation {
  slot: string
  unit: string
  matchup: string
  projected: number
  burn_cost: number
  rationale: string
}

interface Report {
  headline: string
  recommendations: Recommendation[]
  hold: { unit: string; until: string; why: string }[]
  risk: string
}

/**
 * The paid surface. Free users see the shape of the answer and a lock.
 * The value proposition is season-long allocation, not a single week's start/sit —
 * only this game has a burn budget, so nobody else can give this advice.
 */
export default function Insights({
  leagueId, weekId, memberId,
}: { leagueId: string; weekId: string | null; memberId: string | null }) {
  const [tier, setTier] = useState<string>('free')
  const [report, setReport] = useState<Report | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    ;(async () => {
      const { data } = await supabase.from('subscriptions').select('tier').maybeSingle()
      setTier(data?.tier ?? 'free')
    })()
  }, [])

  const run = async () => {
    if (!weekId || !memberId) return
    setBusy(true); setErr(null)
    const { data: sess } = await supabase.auth.getSession()
    try {
      const res = await fetch('/.netlify/functions/insights', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${sess.session?.access_token ?? ''}`,
        },
        body: JSON.stringify({ leagueId, weekId, memberId }),
      })
      if (!res.ok) throw new Error(await res.text())
      setReport(await res.json())
    } catch (e: any) {
      setErr(e.message)
    } finally {
      setBusy(false)
    }
  }

  if (tier === 'free') {
    return (
      <div className="card paywall">
        <h3>Gridlock Edge</h3>
        <p>
          Anyone can tell you who to start this week. Only we can tell you what
          starting them <em>costs you in January</em>.
        </p>
        <ul>
          <li>Season-long burn plan across all 18 weeks</li>
          <li>Scarcity pricing — which units to spend now vs. bank</li>
          <li>Weekly board with projected unit totals and matchup context</li>
          <li>Playoff-phase reset planning</li>
        </ul>
        <button className="btn btn-primary">Upgrade — $6/mo</button>
        <p className="muted small">League play stays free, forever.</p>
      </div>
    )
  }

  return (
    <div className="insights">
      <button className="btn btn-primary" onClick={run} disabled={busy}>
        {busy ? 'Thinking…' : 'Generate this week\'s plan'}
      </button>
      {err && <p className="error">{err}</p>}
      {report && (
        <>
          <h3>{report.headline}</h3>
          <table className="table">
            <thead>
              <tr><th>Slot</th><th>Unit</th><th>Matchup</th><th>Proj</th><th>Burn cost</th><th>Why</th></tr>
            </thead>
            <tbody>
              {report.recommendations.map((r, i) => (
                <tr key={i}>
                  <td>{r.slot}</td>
                  <td><strong>{r.unit}</strong></td>
                  <td>{r.matchup}</td>
                  <td>{r.projected}</td>
                  <td>{r.burn_cost}</td>
                  <td className="why">{r.rationale}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <h4>Hold these</h4>
          <ul>
            {report.hold.map((h, i) => (
              <li key={i}><strong>{h.unit}</strong> until {h.until} — {h.why}</li>
            ))}
          </ul>
          <p className="muted">{report.risk}</p>
        </>
      )}
    </div>
  )
}
